import { motion } from "framer-motion";
import {
  Compass,
  Lightbulb,
  Handshake,
  TrendingUp,
  ShieldCheck,
  Sparkles,
  Users,
  Award,
  Target,
} from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { MISSION_ITEMS, VALUES, STATS } from "@/lib/eraContent";

const MISSION_ICONS = { Compass, Lightbulb, Handshake, TrendingUp };
const VALUE_ICONS = { ShieldCheck, Sparkles, Users, Award };

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.55, ease: "easeOut" },
  }),
};

export const About = () => {
  const { t } = useLang();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: intro */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xs uppercase tracking-[0.28em] font-semibold text-navy-900"
            >
              {t.about.eyebrow}
            </motion.span>
            <motion.h2
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-navy-900 leading-[1.05]"
            >
              {t.about.title}
              <br />
              <span className="text-navy-500">{t.about.titleAccent}</span>
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={1}
              variants={fadeUp}
              className="mt-6 text-navy-700/70 text-base leading-relaxed"
            >
              {t.about.body}
            </motion.p>

            {/* Vision card inline */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={2}
              variants={fadeUp}
              className="mt-10 rounded-3xl border border-navy-100 bg-navy-50/60 p-6"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl bg-navy-900 text-white flex items-center justify-center">
                  <Target className="w-5 h-5" />
                </div>
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-navy-900">
                  {t.about.visionLabel}
                </div>
              </div>
              <p className="mt-4 text-sm text-navy-700 leading-relaxed">
                {t.about.vision}
              </p>
            </motion.div>
          </div>

          {/* Right: mission + values */}
          <div className="lg:col-span-7 space-y-10">
            {/* Mission grid */}
            <div>
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                className="mb-6 text-xs uppercase tracking-[0.24em] font-semibold text-navy-900"
              >
                {t.about.missionLabel}
              </motion.div>
              <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
                {MISSION_ITEMS.map((item, i) => {
                  const Icon = MISSION_ICONS[item.icon] || Compass;
                  const copy = t.about.mission[item.key];
                  return (
                    <motion.div
                      key={item.key}
                      data-testid={`mission-card-${item.key}`}
                      initial="hidden"
                      whileInView="show"
                      viewport={{ once: true, amount: 0.2 }}
                      custom={i}
                      variants={fadeUp}
                      className="group rounded-3xl bg-white border border-navy-100 hover:border-navy-200 p-6 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(13,27,62,0.18)]"
                    >
                      <div className="h-12 w-12 rounded-2xl bg-gold/15 text-navy-900 flex items-center justify-center transition-colors group-hover:bg-gold">
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className="mt-6 font-display text-lg md:text-xl font-semibold tracking-tight text-navy-900">
                        {copy.title}
                      </h3>
                      <p className="mt-2 text-sm text-navy-700/70 leading-relaxed">
                        {copy.desc}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Values row */}
            <div>
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                className="mb-6 text-xs uppercase tracking-[0.24em] font-semibold text-navy-900"
              >
                {t.about.valuesLabel}
              </motion.div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {VALUES.map((v, i) => {
                  const Icon = VALUE_ICONS[v.icon] || ShieldCheck;
                  const copy = t.about.values[v.key];
                  return (
                    <motion.div
                      key={v.key}
                      data-testid={`value-card-${v.key}`}
                      initial="hidden"
                      whileInView="show"
                      viewport={{ once: true, amount: 0.2 }}
                      custom={i}
                      variants={fadeUp}
                      className="rounded-2xl bg-navy-50/50 border border-navy-100 p-5 hover:bg-white transition-all duration-300"
                    >
                      <Icon className="w-5 h-5 text-navy-900" />
                      <div className="mt-4 font-display text-sm font-semibold text-navy-900">
                        {copy.title}
                      </div>
                      <div className="mt-1 text-xs text-navy-700/70 leading-relaxed">
                        {copy.desc}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 md:mt-20 relative rounded-3xl bg-gradient-to-br from-navy-900 via-navy-900 to-navy-950 p-8 md:p-12 overflow-hidden"
        >
          <div className="absolute -right-16 -top-16 w-72 h-72 bg-gold/10 rounded-full blur-3xl" />
          <div className="absolute -left-20 bottom-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, i) => (
              <div key={stat.key} data-testid={`stats-bar-${i}`}>
                <div className="font-display text-4xl md:text-5xl font-bold text-white tracking-tighter">
                  {stat.value}
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                  {t.stats[stat.key]}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
